import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { StorageService } from '../services/storage.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { IdleService } from '../core/services/idle.service';

const CSRF_TOKEN_STORAGE_KEY = 'csrfToken';
const CSRF_SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private accountService: AccountService,
    private storageService: StorageService,
    private router: Router,
    private idleService: IdleService
  ) {}

  /**
   * Intercepts all HTTP requests and adds the following headers:
   * - Regulated: A boolean indicating whether the user has completed the KYC process
   * - Chain-id: The chain ID of the user's wallet
   * - X-CSRF-Token: The session CSRF token, on state-changing backend requests only
   *
   * @param request The HTTP request to intercept
   * @param next The next HTTP handler in the chain
   * @returns An observable of the intercepted HTTP event
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const regulated = Boolean(this.storageService.getItemSession('isRegulated'));

    const accountData = this.storageService.getItem('wagmi_new_store');
    const account = accountData ? JSON.parse(accountData) : {
      account: '',
      chainId: environment.SUPPORTED_CHAINS[0],
      isConnected: false
    };

    const modifiedHeaders: any = {
      'Regulated': String(regulated),
      'Chain-id': account.chainId?.toString() || environment.SUPPORTED_CHAINS[0].toString()
    };

    const isBackendRequest = this.isBackendRequest(request.url);

    // Attach the CSRF token only to backend requests so it never leaks to
    // third-party hosts (RPC nodes, IPFS gateways, ...).
    if (isBackendRequest && !CSRF_SAFE_METHODS.includes(request.method.toUpperCase())) {
      const csrfToken = this.storageService.getItem(CSRF_TOKEN_STORAGE_KEY);
      if (csrfToken) {
        modifiedHeaders['X-CSRF-Token'] = csrfToken;
      }
    }

    const modifiedRequest = request.clone({
      setHeaders: modifiedHeaders,
      withCredentials: true
    });

    return next.handle(modifiedRequest).pipe(
      tap((event) => {
        // Capture the session CSRF token from any backend response carrying
        // one (wallet verify, auth/csrf-token).
        if (isBackendRequest && event instanceof HttpResponse) {
          const csrfToken = event.body?.data?.csrf_token;
          if (csrfToken) {
            // Guard against storage exceptions (quota, disabled storage) so a
            // successful HTTP response is never turned into an app error.
            try {
              this.storageService.setItem(CSRF_TOKEN_STORAGE_KEY, csrfToken);
            } catch (_error) {
              // ignore — best effort persistence
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {

        if (error.status === 401) {
          this.handleUnauthorized();
        }

        if (error.status === 403 && this.isCsrfRejection(error)) {
          this.handleUnauthorized();
        }

        return throwError(() => error);
      })
    );
  }

  private isBackendRequest(url: string): boolean {
    return [
      environment.API_BASE_URL,
      environment.API_BASE_URL_V2,
      environment.API_BASE_URL_V3
    ].some((baseUrl) => Boolean(baseUrl) && url.startsWith(baseUrl));
  }

  private isCsrfRejection(error: HttpErrorResponse): boolean {
    return String(error.error?.message || '').toLowerCase().includes('csrf');
  }

  /**
   * Handles unauthorized error by logging out the user.
   * The backend no longer accepts the session (expired cookie, revoked token),
   * so it is treated as an expired session: toast, logout, disconnect wallet.
   */
  private handleUnauthorized() {
    this.idleService.handleSessionExpired();
  }
}
